import { useEffect, useRef } from "react";
import { useWebSocketContext } from "@/context/WebSocketContext";
import useAuth from "@/hooks/useAuth";

/**
 * Hook to manage WebSocket subscription/unsubscription for a company
 * Automatically subscribes when connected and unsubscribes on unmount
 */
export const useCompanySubscription = () => {
  const { auth } = useAuth();
  const { isConnected, sendMessage, subscribeMessage } = useWebSocketContext();
  const companyId = auth?.clientId;
  const hasSubscribed = useRef(false);

  useEffect(() => {
    if (!companyId) return;

    const handleBeforeUnload = () => {
      sendMessage({
        action: "unsubscribe_company",
        company_id: companyId,
      });
    };

    if (isConnected && !hasSubscribed.current) {
      subscribeMessage({
        action: "subscribe_company",
        company_id: companyId,
      });
      hasSubscribed.current = true;
    }
    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
      if (isConnected) {
        sendMessage({
          action: "unsubscribe_company",
          company_id: companyId,
        });
        hasSubscribed.current = false;
      }
    };
  }, [isConnected, sendMessage, subscribeMessage, companyId]);

  return { companyId, isConnected };
};

export default useCompanySubscription;
